import { useState, useEffect } from "react";
import { Dialog, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useCreateUserPolicy, useUpdateUserPolicy } from "@/api/devices";
import type { UserDevicePolicy } from "@/api/types";

interface Props {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  deviceId: string;
  assignmentId: string;
  userEmail: string;
  policy?: UserDevicePolicy | null;
}

export function UserPolicyModal({ open, onClose, tenantId, deviceId, assignmentId, userEmail, policy }: Props) {
  const isEdit = !!policy;
  const create = useCreateUserPolicy(tenantId, deviceId);
  const update = useUpdateUserPolicy(tenantId, deviceId);
  const isPending = create.isPending || update.isPending;

  const [lastLoginAt, setLastLoginAt] = useState("");
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [timeoutMs, setTimeoutMs] = useState(0);

  useEffect(() => {
    if (open) {
      setLastLoginAt(policy?.last_login_at ? policy.last_login_at.slice(0, 16) : "");
      setRequiresPassword(policy?.screenlock_settings?.requiresPassword ?? false);
      setTimeoutMs(policy?.screenlock_settings?.screenSleepTimeoutMs ?? 0);
    }
  }, [open, policy]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      assignment_id: assignmentId,
      last_login_at: lastLoginAt ? new Date(lastLoginAt).toISOString() : null,
      settings_requires_password: requiresPassword,
      settings_screen_sleep_timeout_ms: timeoutMs,
    };
    const onSuccess = { onSuccess: onClose };
    if (isEdit) {
      update.mutate({ id: policy.id, payload }, onSuccess);
    } else {
      create.mutate({ payload }, onSuccess);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{isEdit ? "Edit" : "Add"} Policy for {userEmail}</DialogTitle>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Last Login</label>
          <Input type="datetime-local" value={lastLoginAt} onChange={e => setLastLoginAt(e.target.value)} />
        </div>
        <div className="flex items-center justify-between">
          <label className="text-sm text-text-muted">Requires Password</label>
          <Switch checked={requiresPassword} onChange={setRequiresPassword} />
        </div>
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Screen Sleep Timeout (ms)</label>
          <Input type="number" min={0} value={timeoutMs} onChange={e => setTimeoutMs(Number(e.target.value))} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>Cancel</Button>
          <Button type="submit" disabled={isPending}>{isPending ? "Saving..." : isEdit ? "Update" : "Add"}</Button>
        </div>
      </form>
    </Dialog>
  );
}
